<script lang="ts">
import { ref } from 'vue'

// Module-level so the "checking for lead" in-flight guard survives this
// component being unmounted/remounted (e.g. opening then closing a tab).
const isChecking = ref(false)
</script>

<script setup lang="ts">
import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import axios from '@/api'
import BaseButton from '@/components/BaseButton.vue'
import LeadAvatar from '@/components/LeadAvatar.vue'
import type { Lead, CallinglyCall } from '@/types/types'
import { useStatusStore } from '@/stores/status'
import { useOnlineStore } from '@/stores/online'
import { useRegisteredStore } from '@/stores/registered'

const props = defineProps<{
  lead: Partial<Lead>
  call: CallinglyCall | null
  message: string
}>()

// Only some leads have a Gravatar; for the rest Gravatar serves our own
// initials avatar, the same one LeadAvatar draws. So draw LeadAvatar right
// away and reveal the picture over it once it has loaded — no gap while it's
// in flight, and a lead whose picture never arrives keeps the local avatar.
const photoLoaded = ref(false)

watch(
  () => props.lead.profile_picture,
  () => (photoLoaded.value = false),
)

const { status } = storeToRefs(useStatusStore())
const { online } = storeToRefs(useOnlineStore())
const { registered } = storeToRefs(useRegisteredStore())

const crmLinks: Record<string, string> = {
  hubspot: 'Hubspot',
  salesforce: 'Salesforce',
  activecampaign: 'ActiveCampaign',
  followupboss: 'Follow Up Boss',
}

const check = () => {
  if (isChecking.value) {
    return
  }

  isChecking.value = true

  axios
    .post('/apps/webrtc/check')
    .catch((err) => {
      console.error(err)
    })
    .finally(() => {
      isChecking.value = false
    })
}
</script>

<template>
  <div>
    <div v-if="lead.label" class="relative h-[80px] w-[80px] mx-auto select-none">
      <LeadAvatar :name="lead.label" size="xl" />
      <img
        v-if="lead.profile_picture"
        class="absolute inset-0 h-[80px] w-[80px] rounded-full object-cover"
        :class="photoLoaded ? 'opacity-100' : 'opacity-0'"
        :src="lead.profile_picture"
        @load="photoLoaded = true"
      />
    </div>
    <!-- No lead yet (the idle screen) — a quiet placeholder, not a loud "?". -->
    <div v-else class="h-[80px] w-[80px] rounded-full bg-[#81D5F2] mx-auto select-none" />

    <h3>
      {{ lead.label }}
    </h3>
    <h3 v-if="status == 'waiting'">Waiting for lead...</h3>

    <BaseButton
      v-if="status == 'waiting' && online && registered && !lead.phone_number"
      type="button"
      class="mt-[20px] p-[6px_12px_6px_10px]!"
      :disabled="isChecking"
      @click="check()"
    >
      <i class="material-icons text-[23px]! float-left">person</i
      ><span class="ml-[7px] relative top-[1px]">Check for lead</span>
    </BaseButton>

    <p v-if="status != 'waiting'" class="text-[16px]">
      {{ message }}
    </p>

    <div class="mt-[20px] mb-[20px] text-center">
      <h4 class="italic" v-if="lead.source">
        {{ lead.source }}
      </h4>
      <h4 v-if="call?.account_id == 9920">
        {{ lead.phone_number_formatted }}
      </h4>
      <h4 v-if="lead.email">
        {{ lead.email }}
      </h4>
      <h4 v-if="lead.category">
        {{ lead.category }}
      </h4>
      <a
        v-if="lead.crm && crmLinks[lead.crm]"
        target="_blank"
        class="inline-block px-[12px] py-[6px] mb-0 text-[14px] font-normal leading-[1.42857143] text-center whitespace-nowrap align-middle touch-manipulation cursor-pointer select-none border rounded-[4px] no-underline text-[#333] bg-white border-[#ccc] hover:no-underline hover:text-[#333] hover:bg-[#e6e6e6] hover:border-[#adadad] focus:no-underline focus:text-[#333] focus:bg-[#e6e6e6] focus:border-[#adadad] active:outline-0 active:bg-[#e6e6e6] active:border-[#adadad] active:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)]"
        :href="`https://callingly.com/dashboard/leads/${lead.id}/crm-redirect`"
        >{{ crmLinks[lead.crm] }} &nbsp;<i class="material-icons text-[18px]! float-right mt-[1px]"
          >link</i
        ></a
      >
    </div>
  </div>
</template>
